import * as React from 'react'
import { Column, ColumnOption } from '../types'
import I18n from '../../../../libs/i18n'

interface SelectInputProps {
  column: Column
  value: number | number[] | null
  onChange: (value: number | number[] | null) => void
  fieldId: string
}

function ColorDot({ color }: { color?: string }) {
  if (!color) return null

  return (
    <span
      style={{
        display: 'inline-block',
        width: 10,
        height: 10,
        borderRadius: '50%',
        backgroundColor: color,
        marginRight: 6,
        verticalAlign: 'middle',
      }}
    />
  )
}

function OptionLabel({ option }: { option: ColumnOption }) {
  return (
    <>
      <ColorDot color={option.color} />
      {option.label}
    </>
  )
}

export default function SelectInput({
  column,
  value,
  onChange,
  fieldId,
}: SelectInputProps) {
  const allowMultiselect = column.settings.allow_multiselect

  if (allowMultiselect) {
    return (
      <MultiselectInput
        column={column}
        value={value}
        onChange={onChange}
        fieldId={fieldId}
      />
    )
  }

  return (
    <SingleSelectInput
      column={column}
      value={value}
      onChange={onChange}
      fieldId={fieldId}
    />
  )
}

function MultiselectInput({
  column,
  value,
  onChange,
  fieldId,
}: SelectInputProps) {
  const selectedIds: number[] = Array.isArray(value)
    ? value.map((v) => Number(v))
    : value != null
      ? [Number(value)]
      : []

  return (
    <div role="group" aria-labelledby={`${fieldId}-label`}>
      {column.column_options.map((option) => {
        const htmlId = `${fieldId}_${option.id}`
        const isChecked = selectedIds.includes(option.id)

        return (
          <div key={option.id} className="form-check">
            <input
              type="checkbox"
              id={htmlId}
              className="form-check-input"
              checked={isChecked}
              onChange={(e) => {
                if (e.target.checked) {
                  onChange([...selectedIds, option.id])
                } else {
                  onChange(selectedIds.filter((id) => id !== option.id))
                }
              }}
            />
            <label htmlFor={htmlId} className="form-check-label">
              <OptionLabel option={option} />
            </label>
          </div>
        )
      })}
    </div>
  )
}

function SingleSelectInput({
  column,
  value,
  onChange,
  fieldId,
}: SelectInputProps) {
  const selectedId = value != null ? Number(value) : null

  return (
    <div role="radiogroup" aria-labelledby={`${fieldId}-label`}>
      {!column.required && (
        <div className="form-check">
          <input
            type="radio"
            id={`${fieldId}_none`}
            name={fieldId}
            className="form-check-input"
            checked={selectedId === null}
            onChange={() => onChange(null)}
          />
          <label htmlFor={`${fieldId}_none`} className="form-check-label text-muted">
            {I18n.t('project_tickets.form.custom_fields.no_selection')}
          </label>
        </div>
      )}
      {column.column_options.map((option) => {
        const htmlId = `${fieldId}_${option.id}`

        return (
          <div key={option.id} className="form-check">
            <input
              type="radio"
              id={htmlId}
              name={fieldId}
              className="form-check-input"
              checked={selectedId === option.id}
              onChange={() => onChange(option.id)}
              required={column.required && selectedId === null}
            />
            <label htmlFor={htmlId} className="form-check-label">
              <OptionLabel option={option} />
            </label>
          </div>
        )
      })}
    </div>
  )
}
